import React, { useState, useCallback, useEffect, useMemo } from "react"
import Alert from '@qc/particle/lib/components/alert'
import Button from '@qc/particle/lib/components/button'
import Form from '@qc/particle/lib/components/form'
import Input from '@qc/particle/lib/components/input'
import Result from '@qc/particle/lib/components/result'
import { PSClickWrap } from '@pactsafe/pactsafe-react-sdk'
import PasswordInput, { PASSWORD_VALIDATOR } from "../components/PasswordInput"

import { connect } from "react-redux";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEnvelope } from '@fortawesome/pro-light-svg-icons/faEnvelope'

import { getForwardUrl } from "../oktaConfigUtil"
import { getValidRedirect } from '../utils/whiteListRedirect'
import * as Stld from "./SignUpView.styles";
import { registerUser } from "../store/reducers/user_registration/actions"
import CaptchaProtectedComponent from "../components/CaptchaProtectedComponent"
import QCPanelContainer from "../components/QCPanelContainer";
import { ORG_TIERS } from "../constants/signup";
import OktaAuth from "@okta/okta-auth-js";
import { oktaConfig } from "../config/okta";
import { ROUTES } from "../constants/routes";
import { checkLoggedInAndRedirect } from "../utils/loggedInCheckRedirect"
import { QuantcastLogo } from "./ProductMatrix.styles";
import { executeRules } from "../constants/password"

const successMessageView = (success, idpInitiated, email) => {
  if(!success) {
    return null;
  }
  if (idpInitiated) {
    return (
      <>
      <Result
        status="success"
        title="Ready to go!"
        subTitle="Your email is configured to an external identity provider. You can directly login with your email."
        extra={[<Button type="primary" href="/user/login">Go to Login</Button>]} />
      </>
    )
  }

  return (
    <Result
      title="You have signed up"
      icon={<FontAwesomeIcon icon={faEnvelope}/>}
      subTitle={
        <>
          <span>
            Check your email: <strong>{email}</strong> and follow the instruction.
          </span>
        </>
      }
      extra={[<Button type="primary" href="/selfserve/login">Back to Login</Button>]}
    />
  )
}

export const MarketerSignUpViewBase = ({
  saving,
  error,
  success,
  idpInitiated,
  registerUser,
  testMode
}) => {
  const urlParams = new URLSearchParams(location.search);
  const tier = urlParams.get('tier');
  if (!tier || ['advertise_free', 'prospecting_advertise'].indexOf(tier.toLowerCase()) < 0) {
    window.location = window.origin || "https://www.quantcast.com";
  }
  const isSalesProspecting = (tier === ORG_TIERS.PROSPECTING_ADVERTISE)

  const [form] = Form.useForm()
  const forwardUrl = getForwardUrl()
  const redirectUrl = getValidRedirect(forwardUrl);
  const [recaptchaToken, setRecatpchaToken] = useState(null)
  const [recaptchaVersion, setRecatpchaVersion] = useState(null)
  const [signed, setSigned] = useState(testMode ? true : false)
  const [formValid, setFormValid] = useState(false)
  const [username, setUsername] = useState(null)

  const _registerUser = (formData) => {
    
    registerUser(
      formData.email,
      formData.firstName,
      formData.lastName,
      redirectUrl,
      recaptchaVersion,
      recaptchaToken,
      formData.password,
      JSON.stringify({
        orgName: isSalesProspecting ? `${formData.firstName} ${formData.lastName} Sales Prospecting` : formData.orgName,
        orgTier: tier,
      })
    );
  };

  const onPass = useCallback((version, token) => {
    setRecatpchaVersion(version)
    setRecatpchaToken(token)
  }, [setRecatpchaToken, setRecatpchaVersion])

  let errorMessage = null
  if (error === "403") {
    errorMessage = <Alert message="Sorry, there was a problem with recatcpha. Please reload and try again" type="error" showIcon data-cy="errorMessageTokenExpired" />
  } else if (error === "409") {
    errorMessage = <Alert message="Sorry, this email is already taken. Please try another one." type="warning" showIcon data-cy="errorMessageTokenExpired" />
  } else if (error) {
    errorMessage = <Alert message="An error occurred when trying to activate your account. Please try again later." type="error" showIcon />
  }

  const successMessage = successMessageView(success, idpInitiated, form.getFieldValue('email'))

  // Turns out it's impossible to check whether or not an antd form is valid or not, is this okay for now?
  const PASSWORD_REGEX = /^^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W])[A-Za-z\d\W]{8,}$/
  const SALES_PROSPECTING_EMAIL_REGEX = /^[A-Za-z0-9._%+-]+\+[A-Za-z0-9._%+-]+@quantcast\.com$/ // Forcing subaddressing
  const EMAIL_REGEX = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/

  const updateFormValidity = (updatedValues, allValues) => {
    setUsername(allValues.email)

    if (allValues.firstName?.length > 1 && 
      allValues.lastName?.length > 1 && 
      !executeRules(allValues.email, allValues.password).includes(false) &&
      allValues.password === allValues.confirmPassword &&
      ((isSalesProspecting && SALES_PROSPECTING_EMAIL_REGEX.test(allValues.email)) || 
        (!isSalesProspecting && EMAIL_REGEX.test(allValues.email) && allValues?.orgName?.length > 2)
      )) {
        setFormValid(true)
      } else {
        setFormValid(false)
      }
  }

  return (
    <QCPanelContainer showMarketingMessage={false}>
      <CaptchaProtectedComponent action="register" onPass={onPass} onFail={() => console.log("Captcha Failed")}>
        <Stld.Content>
          <Stld.QuantcastLogo />
          <Stld.CardLarge>
            <div>
              {!successMessage ? <div data-cy="header" style={{textAlign: "center"}}>
                <Stld.H3 data-cy="qText">Sign Up</Stld.H3>
              </div> : null}
              {errorMessage}
              {successMessage ? successMessage : 
                <Form
                  requiredMark={false}
                  form={form}
                  layout="vertical"
                  onFinish={_registerUser}
                  onValuesChange={updateFormValidity}
                >
                  <div style={{width: "100%", display:"flex", gap: "10px"}}>
                    <Stld.FormControl
                      style={{width: "50%"}}
                      data-cy="firstNameControl"
                      label="First Name"
                      name="firstName"
                      rules={[
                        {required: true, message: 'Please input your first name' },
                        {whitespace: true, message: 'First name cannot be a whitespace'},
                        {min: 2, message: "Names must have a minimum of two characters"}
                      ]}
                    >
                      <Input data-testid="firstNameInput" placeholder="Type in your name" size="large" />
                    </Stld.FormControl>
                    <Stld.FormControl
                      style={{width: "50%"}}
                      data-cy="lastNameControl"
                      label="Last Name"
                      name="lastName"
                      rules={[
                        {required: true, message: 'Please input your last name' },
                        {whitespace: true, message: 'First name cannot be a whitespace'},
                        {min: 2, message: "Names must have a minimum of two characters"}
                      ]}
                    >
                      <Input data-testid="lastNameInput" placeholder="Type in your last name" size="large" />
                    </Stld.FormControl>
                  </div>
                  <Stld.FormControl
                    data-cy="emailAddressControl"
                    label={isSalesProspecting ? "Quantcast Email" : "Email"}
                    name="email"
                    validateTrigger="onBlur"
                    style = {{ height: "92px", marginBottom: "0px" }} // This fixes the annoying 'bounce' that happens when you fix a problem with your email address
                    rules={[
                      {required: true, message: 'Email address is not valid' },
                      (isSalesProspecting ? 
                        {
                          pattern: new RegExp(SALES_PROSPECTING_EMAIL_REGEX), 
                          message: "Use a subaddressed Quantcast email (e.g. john.doe+sales@quantcast.com)"
                        } : {type: "email", message: 'Email address is not valid'}
                      )
                    ]}
                  >
                    <Input data-testid="emailInput" id="emailInput" placeholder="Type in your email address" size="large" />
                  </Stld.FormControl>

                  { !isSalesProspecting ?
                    <Stld.FormControl
                      data-cy="orgNameControl"
                      label="Organization Name"
                      name="orgName"
                      validateTrigger="onBlur"
                      rules={[
                        { required: true, message: 'Please input your organization name' },
                        { whitespace: true, message: 'Name can not be blank' },
                        { min: 3, message: "Organization name must be 3 or more characters long "}
                      ]}
                    >
                      <Input data-testid="orgNameInput" id="orgNameInput" placeholder="Type in your organization name" size="large" />
                    </Stld.FormControl> : null
                  }
                  
                  <div style={{width: "100%", display:"flex", gap: "10px"}}>
                    <Stld.FormControl
                      style={{width: "50%", height: "214px", marginBottom: "0px"}} // Specifying height and margin fixes 'bounce'
                      data-cy="passwordControl"
                      label="Password"
                      name="password"
                      validateTrigger="onChange"
                      rules={[
                        {required: true, message: ''},
                        (form) => ({
                          validator(_, value) {
                            const email = form.getFieldValue('email');
                            if (PASSWORD_VALIDATOR(email)()) {
                              return Promise.resolve();
                            } else {
                              return Promise.reject();
                            }
                          },
                          message: 'The two passwords that you entered do not match'
                        }),
                      ]}
                    >
                      <PasswordInput data-testid="passwordInput" placeholder="Type in your password" size="large" username={username} />
                    </Stld.FormControl>
                    <Stld.FormControl
                      style={{width: "50%"}}
                      data-cy="confirmPasswordControl"
                      label="Confirm Password"
                      name="confirmPassword"
                      dependencies={['password']}
                      rules={[
                        {required: true, message: 'Please confirm your password' },
                        (form) => ({
                          validator(_, value) {
                            if (!value || form.getFieldValue('password') === value) {
                              console.log("resolving password match promise")
                              return Promise.resolve();
                            }
                            return Promise.reject();
                          },
                          message: 'The two passwords that you entered do not match'
                        }),
                      ]}
                    >
                      <PasswordInput data-testid="confirmPasswordInput" placeholder="Type in your password" size="large" hideRules />
                    </Stld.FormControl>
                  </div>

                  {!isSalesProspecting ?
                    <PSClickWrap
                      clickWrapStyle="combined"
                      accessId={process.env.REACT_APP_PACT_SAFE_ACCESS_ID || ''}
                      groupKey={process.env.REACT_APP_PACT_SAFE_ADVERTISE_GROUP_KEY || ''}
                      signerIdSelector={"emailInput"}
                      onValid={() => setSigned(true)}
                      onInvalid={() => setSigned(false)}
                    />
                    :
                    null
                  }
                  
                  <Stld.FormControl style={{margin: "0 auto 0 auto", width: "60%"}}>
                    <Button data-testid="submit" type="primary" htmlType="submit" loading={saving} disabled={saving || !formValid || !(isSalesProspecting || signed)} size="large">
                      Sign Up
                    </Button>
                  </Stld.FormControl>
                  <div style={{textAlign: "center", paddingTop: "10px"}}>
                    Already registered? <a href="/selfserve/login">Log In</a>
                  </div>
                </Form>
              }
            </div>
          </Stld.CardLarge>
        </Stld.Content>
      </CaptchaProtectedComponent>
    </QCPanelContainer>
  );
};

const mapStateToProps = state => ({
  saving: state.registration.loading,
  registration: state.registration,
  error: state.registration.error,
  success: state.registration.success,
  idpInitiated: state.registration.idpInitiated
})

const mapDispatchToProps = dispatch => ({
  registerUser: (email, firstName, lastName, redirectUrl, recaptchaVersion, recaptchaToken, password, signUpAttributes) =>
    dispatch(registerUser(email, firstName, lastName, redirectUrl, recaptchaVersion, recaptchaToken, password, signUpAttributes)),
})

const MarketerSignUpView = connect(mapStateToProps, mapDispatchToProps)(MarketerSignUpViewBase)

export default MarketerSignUpView
